/**
 * Parwaaz — card payload, Ed25519 signing/verification and the pilot-facing verdict.
 * The payload is a small pipe-delimited string (fits comfortably in a scannable QR); the same
 * bytes are signed in the office browser and verified everywhere else (offline verify app,
 * backend, gate scanner), so signing/verification must be byte-identical and synchronous.
 */
import * as ed from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import { b64urlDecode, b64urlEncode, utf8Bytes } from './b64';
import type { RevocationList, TenantKey } from './types';

// @noble/ed25519 v2's sync API (`sign`/`verify`/`getPublicKey`) needs a synchronous SHA-512.
// This must run identically in the browser, Node and Apps Script's V8 — all pure JS, no I/O.
ed.etc.sha512Sync = (...m: Uint8Array[]) => sha512(ed.etc.concatBytes(...m));

export const PAYLOAD_VERSION = 'FFC1';

export interface CardPayloadInput {
  cardNo: string;
  name: string; // already transliterated via names.translitForCard
  nationality: string;
  validFrom: string;
  validTo: string;
  cert: string;
  photoHash12: string;
  keyId: string;
}

const PAYLOAD_FIELD_COUNT = 9; // version + 8 CardPayloadInput fields

/** Build the exact string that gets signed, e.g. `FFC1|BB-2026-000123|MUELLER, ANNA|DEU|2026-10-01|2026-11-30|IPPI-4|3f9a1c8e2b41|k1`. */
export function buildPayload(i: CardPayloadInput): string {
  const cert = i.cert.replace(/\|/g, '');
  return [PAYLOAD_VERSION, i.cardNo, i.name, i.nationality, i.validFrom, i.validTo, cert, i.photoHash12, i.keyId].join('|');
}

/** Parse a scanned QR payload (`<payload>|<signature>`) back into its parts. `null` if malformed. */
export function parsePayload(qr: string): { payload: string; signature: string; fields: CardPayloadInput } | null {
  const parts = qr.split('|');
  if (parts.length !== PAYLOAD_FIELD_COUNT + 1) return null;
  if (parts[0] !== PAYLOAD_VERSION) return null;
  const [, cardNo, name, nationality, validFrom, validTo, cert, photoHash12, keyId, signature] = parts;
  return {
    payload: parts.slice(0, PAYLOAD_FIELD_COUNT).join('|'),
    signature,
    fields: { cardNo, name, nationality, validFrom, validTo, cert, photoHash12, keyId },
  };
}

/** Sign `payload` with the Ed25519 seed (base64url), returning a base64url signature. */
export function signPayload(payload: string, seedB64url: string): string {
  const sig = ed.sign(utf8Bytes(payload), b64urlDecode(seedB64url));
  return b64urlEncode(sig);
}

/** Verify a base64url signature over `payload` with a base64url public key. */
export function verifyPayload(payload: string, signatureB64url: string, publicKeyB64url: string): boolean {
  try {
    return ed.verify(b64urlDecode(signatureB64url), utf8Bytes(payload), b64urlDecode(publicKeyB64url));
  } catch {
    return false;
  }
}

/** Generate a new Ed25519 seed/public key pair. `randomBytes` is injected so callers control entropy (and tests can be deterministic). */
export function generateSeed(randomBytes: (n: number) => Uint8Array): { seedB64url: string; publicKeyB64url: string } {
  const seed = randomBytes(32);
  const publicKey = ed.getPublicKey(seed);
  return { seedB64url: b64urlEncode(seed), publicKeyB64url: b64urlEncode(publicKey) };
}

/** Derive the base64url public key from a base64url seed. */
export function publicKeyFromSeed(seedB64url: string): string {
  return b64urlEncode(ed.getPublicKey(b64urlDecode(seedB64url)));
}

/** First 12 hex characters of a SHA-256 hex digest — a short, non-reversible photo fingerprint for the payload. */
export function photoHash12(sha256Hex: string): string {
  return sha256Hex.slice(0, 12);
}

/** Stable JSON for signing a revocation list: keys in a fixed order so signer and verifier agree byte-for-byte. */
export function canonicalRevocationList(l: Omit<RevocationList, 'signature'>): string {
  return JSON.stringify({
    version: l.version,
    generatedAt: l.generatedAt,
    keyId: l.keyId,
    revoked: l.revoked.map((r) => ({
      cardNo: r.cardNo,
      at: r.at,
      ...(r.reason !== undefined ? { reason: r.reason } : {}),
    })),
  });
}

export type Verdict = { colour: 'green' | 'amber' | 'red'; title: string; detail?: string; fields?: CardPayloadInput };

export interface VerifyCardOpts {
  today: string;
  revoked: Set<string>;
  listAgeSeconds: number;
  staleAfterHours: number;
}

/** The full offline verification decision for a scanned QR string. */
export function verifyCard(qr: string, keys: TenantKey[], opts: VerifyCardOpts): Verdict {
  const parsed = parsePayload(qr);
  if (!parsed) {
    return { colour: 'red', title: 'Invalid card', detail: 'QR content could not be read.' };
  }
  const key = keys.find((k) => k.kid === parsed.fields.keyId);
  if (!key) {
    return { colour: 'red', title: 'Unknown signing key', detail: `Key '${parsed.fields.keyId}' is not recognised.` };
  }
  if (!verifyPayload(parsed.payload, parsed.signature, key.publicKey)) {
    return { colour: 'red', title: 'Signature invalid — card altered or forged', fields: parsed.fields };
  }
  const { validFrom, validTo, cardNo } = parsed.fields;
  if (opts.today < validFrom) {
    return { colour: 'red', title: 'Not yet valid', fields: parsed.fields };
  }
  if (opts.today > validTo) {
    return { colour: 'red', title: 'Expired', fields: parsed.fields };
  }
  if (opts.revoked.has(cardNo)) {
    return { colour: 'red', title: 'REVOKED', fields: parsed.fields };
  }
  if (opts.listAgeSeconds > opts.staleAfterHours * 3600) {
    const hours = Math.round(opts.listAgeSeconds / 3600);
    return { colour: 'amber', title: `Valid — revocation list is ${hours} h old`, fields: parsed.fields };
  }
  if (opts.today === validTo) {
    return { colour: 'amber', title: 'Valid — expires today', fields: parsed.fields };
  }
  return { colour: 'green', title: 'Valid', fields: parsed.fields };
}
